<template>
  <div
    v-if="show"
    class="view-as-banner d-print-none"
    :class="{ 'view-as-banner--warning': warning }"
    role="status"
    aria-live="polite"
  >
    <!-- Two thirds of the viewport, centered. Deliberately not .container: that class
         carries Bootstrap's clearfix ::before/::after, which become flex items here and
         eat the space-between edges. -->
    <div class="view-as-banner__inner d-flex align-center justify-space-between flex-wrap py-2">
      <div class="d-flex align-center text-truncate">
        <v-icon size="18" class="me-2">mdi-eye-outline</v-icon>

        <span class="text-body-2 text-truncate">
          Viewing as <strong>{{ label }}</strong
          ><template v-for="detail in details" :key="detail"> - {{ detail }}</template>
        </span>
      </div>

      <span v-if="countdownLabel" class="text-body-2 ms-4" aria-live="off">
        Session expires in <strong>{{ countdownLabel }}</strong>
      </span>

      <span class="d-sr-only" aria-live="assertive">{{ warningAnnouncement }}</span>

      <div class="d-flex align-center">
        <v-btn
          v-if="warning"
          variant="flat"
          size="x-small"
          color="black"
          class="font-weight-bold rounded-pill ms-4"
          :loading="renewalPending"
          @click="keepViewing"
        >
          Keep viewing
        </v-btn>

        <v-btn
          variant="flat"
          size="x-small"
          color="white"
          class="text-black font-weight-bold rounded-pill ms-4"
          @click="exitViewAs"
        >
          Exit view as
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useViewAsStore } from '@/stores/viewAs';
import { useUserStore } from '@/stores/user';

// Persistent view-as indicator. Sticks to the top of the viewport on every page so
// scrolling, navigating, or refreshing can never hide the fact that the portal is
// showing someone else's data. The exit is delegated to SecondaryHeaderComponent
// (via the store) so this button and "Stop view as" run the identical teardown.
//
// ACC-228: also owns enforcing the impersonation token's expiry. Ticking off the
// absolute expiresAt stored in Pinia (rather than a one-shot setTimeout scheduled
// once at session start) means the deadline is correct no matter how long this
// component has been mounted for, and gives the same tick a countdown to display.
//
// The time limit is a security control, so the token is never renewed silently: the
// admin is warned near the end and extends it themselves with "Keep viewing".
const WARNING_MS = 5 * 60 * 1000;
// "Keep viewing" can be pressed again after this if the extension never landed.
const RENEW_RETRY_MS = 30 * 1000;

export default defineComponent({
  name: 'ViewAsBanner',

  data() {
    return {
      now: Date.now(),
      tickTimer: null,
      // expiresAt this component has already requested an exit for — guards against
      // re-requesting every tick while the async teardown from the first request
      // is still in flight.
      exitRequestedFor: null,
      // Same guard for "Keep viewing", plus when it was pressed so a failed extension
      // can be retried. A successful one replaces expiresAt, which re-arms both guards.
      renewalRequestedFor: null,
      renewalRequestedAt: null
    };
  },

  computed: {
    show() {
      const authStore = useAuthStore();

      return useViewAsStore().isActive && authStore.isAuthenticated;
    },

    label() {
      return useViewAsStore().targetLabel;
    },

    // ASURITE (or email) and partner, appended inline after the name.
    details() {
      return useViewAsStore().targetDetails;
    },

    impersonationExpiresAt() {
      return useUserStore().impersonationSession.expiresAt;
    },

    remainingMs() {
      if (!this.impersonationExpiresAt) return null;

      return new Date(this.impersonationExpiresAt).getTime() - this.now;
    },

    countdownLabel() {
      if (this.remainingMs === null) return '';

      const totalSeconds = Math.max(0, Math.floor(this.remainingMs / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    },

    warning() {
      return this.remainingMs !== null && this.remainingMs > 0 && this.remainingMs <= WARNING_MS;
    },

    // Constant while the warning shows, so screen readers announce it once, not every tick.
    warningAnnouncement() {
      return this.warning ? 'View As ends in less than 5 minutes. Select Keep viewing to continue.' : '';
    },

    renewalPending() {
      return (
        this.renewalRequestedFor === this.impersonationExpiresAt && this.now - this.renewalRequestedAt < RENEW_RETRY_MS
      );
    },

    viewAsActive() {
      return useViewAsStore().isActive;
    }
  },

  watch: {
    // This component is mounted for the app's whole lifetime (App.vue always renders
    // it), so the tick interval must only run while a session is actually active —
    // otherwise every authenticated user pays a forever-running per-second timer.
    viewAsActive(active) {
      if (active) this.startTicking();
      else this.stopTicking();
    }
  },

  mounted() {
    if (this.viewAsActive) this.startTicking();
  },

  beforeUnmount() {
    this.stopTicking();
  },

  methods: {
    exitViewAs() {
      useViewAsStore().requestExit();
    },

    startTicking() {
      if (this.tickTimer) return;

      this.now = Date.now();
      this.tickTimer = setInterval(this.tick, 1000);
    },

    stopTicking() {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    },

    tick() {
      this.now = Date.now();

      if (this.remainingMs === null || this.remainingMs > 0) return;
      if (this.exitRequestedFor === this.impersonationExpiresAt) return;
      if (!useViewAsStore().isActive) return;

      this.exitRequestedFor = this.impersonationExpiresAt;
      useViewAsStore().requestExit();
    },

    keepViewing() {
      this.now = Date.now();

      // The button can outlive the deadline until the next tick; extending past it
      // would skip the exit that expiry requires.
      if (!this.warning) return;
      if (this.renewalPending) return;
      if (!useViewAsStore().isActive) return;

      this.renewalRequestedFor = this.impersonationExpiresAt;
      this.renewalRequestedAt = this.now;
      useViewAsStore().requestRenewal();
    }
  }
});
</script>

<style lang="scss" scoped>
.view-as-banner {
  position: sticky;
  top: 0;
  /* Above #asu_hdr (200), below Vuetify's overlays (2400+) so dialogs still cover it. */
  z-index: 300;
  background: #8c1d40;
  color: #ffffff;
}

.view-as-banner .v-icon {
  color: #ffffff;
}

/* ASU gold, deliberately unlike the normal maroon so the warning is noticed. */
.view-as-banner--warning,
.view-as-banner--warning .v-icon {
  background: var(--asu-primary-gold, #ffc627);
  color: #191919;
}

/* A slow pulse rather than a flash (WCAG 2.3.1), and none for reduced-motion users. */
@media (prefers-reduced-motion: no-preference) {
  .view-as-banner--warning {
    animation: view-as-warning-pulse 2s ease-in-out infinite;
  }
}

@keyframes view-as-warning-pulse {
  50% {
    box-shadow: 0 0 0 4px rgba(255, 198, 39, 0.45);
  }
}

/* Vuetify's width utilities stop at w-75, so this one needs real CSS. */
.view-as-banner__inner {
  width: 66.6667%;
  margin-inline: auto;
}

/* Two thirds of a phone leaves no room for the name and the exit — go edge to edge. */
@media (max-width: 959px) {
  .view-as-banner__inner {
    width: auto;
    margin-inline: 16px;
  }
}
</style>
